import React from "react";
import { Modal } from "../../components";
import PanelSegmentType from "../../components/panels/PanelSegment/PanelSegment";
import { useCustomQuery, useManualPagination } from "../../utilities/hookutils";
import ViewerList from "../components/Viewers/ViewerList";
import { useSetContactParticipant } from "eSign/components/Participant/hooks";
import SelectContact from "components/AddressBook/SelectContact";
import EditParticipantAsContact from "eSign/components/modals/EditParticipantAsContact";
import { DocumentTypeWithoutUnknown, messageTemplateForESignViewer } from "utilities/documentUtils";
import SectionPanel from "components/panels/SectionPanel/SectionPanel";
import { useShortcutPanel } from "components/Shortcuts/hooks";

interface Props {
  initialViewers: server.dto.ApiResponseWrapper<server.dto.ESignParticipant[]>
  document: server.dto.ESignDocument;
}

const DraftViewerPanel = (props: Props) => {
  const [displayAddViewer, setDisplayAddViewer] = React.useState(false);

  const documentType = props.document.documentType as DocumentTypeWithoutUnknown;

  const [contact, setContact] = React.useState<{
    contactId: number
    contactName: string
    culture: server.dto.Culture
    email: string
  }>(undefined);

  const [listInstruction, setListInstruction] = React.useState<server.dto.ListESignParticipantInstruction>({
    page: props.initialViewers.pagination.page,
    perPage: serverReference.constants.eSignParticipant.viewersPageSize,
    documentId: props.document.documentId,
    role: "viewer"
  });

  const participantsQuery = useCustomQuery({
    queryFn: async () => window.CoreApi.ESign.ListParticipants(listInstruction).then(r => r.data),
    queryKey: [listInstruction],
    initialData: {
      data: props.initialViewers.data,
      pagination: props.initialViewers.pagination,
      hasErrors: false,
      errors: []
    },
    onSuccess: (res) => {
      pagination.setPagination(res.pagination);
    },
    keepPreviousData: true
  });

  const pagination = useManualPagination(1, serverReference.constants.eSignParticipant.viewersPageSize, (pagination) => {
    setListInstruction(prevInstr => ({
      ...prevInstr,
      page: pagination.page,
      perPage: pagination.perPage
    }));
  });

  const onAddClicked = () => {
    setDisplayAddViewer(true);
  };

  const setContactParticipant = useSetContactParticipant(props.document.documentId, "viewer", () => {
    participantsQuery.refetch();
    onCloseModal();
  });

  const onContactSelected = async (contact: server.dto.ContactAddressBookEntry, culture: server.dto.Culture, email: string) => {
    setContact({
      contactId: contact.contactId,
      contactName: contact.name.fullName,
      culture,
      email
    });
  };

  const onCloseModal = () => {
    setContact(undefined);
    setDisplayAddViewer(false);
  };

  const isMaxViewersReached = pagination.total >= serverReference.constants.eSignParticipant.maxViewersLimit;

  const lifeCyclesWhereViewersCanBeAdded: server.dto.DocumentLifeCycles[] = ["draft"];
  const canAddViewers = lifeCyclesWhereViewersCanBeAdded.includes(props.document.lifeCycle.value);

  const shortcuts = useShortcutPanel({
    type: "viewer",
    label: RESX.ESign.Viewers,
    totalItems: pagination.total,
    onAddClicked: canAddViewers ? onAddClicked : undefined,
  });

  return <SectionPanel.Panel
    panelRef={shortcuts.refToScrollTo}
    header={<>
      <PanelSegmentType.Title title={RESX.ESign.Viewers} />
      <PanelSegmentType.SupportQuestionMark articleId={serverReference.supportArticleIds.whatAreViewers} />
      {canAddViewers && <PanelSegmentType.Button
        onClick={onAddClicked}
        disabled={isMaxViewersReached}
        label={RESX.Buttons.Add}
        type="secondary"
      />}
      {pagination.Component}
    </>}
  >
    <ViewerList
      viewers={participantsQuery.data.data}
      onChanged={async () => participantsQuery.refetch()}
      document={props.document}
    />

    <Modal
      isOpen={displayAddViewer && contact == null}
      handleClose={onCloseModal}
      title={RESX.ESign.AddViewer}
      width="small"
      height={750}
    >
      <div className="flex-column grow1 gap" style={{ minHeight: 0, display: contact != null ? "none" : undefined }}>
        <SelectContact
          hasNoCompany
          suggestedApiInstruction={{ itemId: props.document.documentId, mainArea: documentType }}
          onContactSelected={onContactSelected}
        />
      </div>
    </Modal>

    <Modal
      isOpen={displayAddViewer && contact != null}
      handleClose={onCloseModal}
      title={RESX.ESign.AddViewer}
      width="small"
    >
      {contact != null && <EditParticipantAsContact
        contactName={contact.contactName}
        contactId={contact.contactId}
        initialEmail={contact.email}
        initialCompany={null}
        initialCulture={contact.culture}
        initialInvitationSubject={""}
        initialInvitationBody={""}
        saveMutation={setContactParticipant}
        isViewer
        onPreviousStepClicked={() => { setContact(undefined); }}
        messageTemplateType={messageTemplateForESignViewer(documentType)}
      />}
    </Modal>

  </SectionPanel.Panel>;
};

export default DraftViewerPanel;